import {
  AlignmentType,
  Document,
  LevelFormat,
  Packer,
  PageOrientation,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';

import type {
  ProposalDocumentAlign,
  ProposalDocumentField,
  ProposalDocumentSection,
  ProposalDocumentSignature,
  ProposalDocumentStructure,
  ProposalDocumentTable,
} from './structure';
import { ProposalTableLayout, tableLayout } from './layout';
import type {
  DeclarationAlign,
  DeclarationParagraph,
  DeclarationRun,
  DeclarationStructure,
} from './declaration';

// Só o arquivo da proposta; o de cada declaração reaproveita buildDocumentHeader e
// buildSignatureBlock.
const PAGE_MARGINS = { top: 1134, right: 1134, bottom: 1134, left: 1134 };
const FONT = 'Arial';
const SIZE = 20; // meio-ponto: 10pt
const SPACING_TEXT = { after: 80 };
const SPACING_SECTION = { before: 280, after: 120 };

const run = (text: string, options: { bold?: boolean; size?: number } = {}) =>
  new TextRun({ text, font: FONT, size: options.size ?? SIZE, bold: options.bold });

const fieldParagraph = (field: ProposalDocumentField) =>
  new Paragraph({
    spacing: SPACING_TEXT,
    children: [run(`${field.rotulo}: `, { bold: true }), run(field.valor)],
  });

// keepNext: o título nunca fica sozinho no pé da página, longe do conteúdo da seção.
const sectionTitle = (numero: number, titulo: string) =>
  new Paragraph({
    keepNext: true,
    spacing: SPACING_SECTION,
    children: [run(`${numero}. ${titulo.toUpperCase()}`, { bold: true, size: 22 })],
  });

// A4 em twips (1/20 de ponto).
const A4 = { width: 11906, height: 16838 };
const contentWidth = (layout: ProposalTableLayout) =>
  (layout.orientation === 'landscape' ? A4.height : A4.width) -
  PAGE_MARGINS.left -
  PAGE_MARGINS.right;
const CELL_MARGINS = { top: 40, bottom: 40, left: 60, right: 60 };

const ALIGN: Record<ProposalDocumentAlign, typeof AlignmentType[keyof typeof AlignmentType]> = {
  esquerda: AlignmentType.LEFT,
  direita: AlignmentType.RIGHT,
  centro: AlignmentType.CENTER,
};

// Larguras fixas por coluna: só com 100% na tabela, o Word encolhe cada coluna ao mínimo e
// quebra o texto letra a letra.
const columnWidths = (pesos: number[], total: number) => {
  const soma = pesos.reduce((acc, peso) => acc + peso, 0) || 1;
  return pesos.map((peso) => Math.floor((total * peso) / soma));
};

const cell = (
  text: string,
  width: number,
  align: ProposalDocumentAlign,
  size: number,
  header = false,
) =>
  new TableCell({
    width: { size: width, type: WidthType.DXA },
    margins: CELL_MARGINS,
    verticalAlign: VerticalAlign.CENTER,
    shading: header ? { fill: 'EEEEEE' } : undefined,
    children: [
      new Paragraph({
        alignment: header ? AlignmentType.CENTER : ALIGN[align],
        children: [run(text, { bold: header, size })],
      }),
    ],
  });

const table = (
  { colunas, linhas, pesos, alinhamentos }: ProposalDocumentTable,
  layout: ProposalTableLayout,
) => {
  const total = contentWidth(layout);
  const widths = columnWidths(pesos, total);
  const size = layout.fontPt * 2; // docx usa meio-ponto
  const alignOf = (index: number) => alinhamentos[index] ?? 'esquerda';
  return new Table({
    width: { size: total, type: WidthType.DXA },
    columnWidths: widths,
    layout: TableLayoutType.FIXED,
    rows: [
      new TableRow({
        tableHeader: true,
        children: colunas.map((c, i) => cell(c, widths[i], 'centro', size, true)),
      }),
      ...linhas.map(
        (linha) =>
          new TableRow({
            cantSplit: true,
            children: linha.map((valor, i) => cell(valor, widths[i], alignOf(i), size)),
          }),
      ),
    ],
  });
};

export const buildDocumentHeader = (titulo: string) => [
  new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 240 },
    children: [run(titulo, { bold: true, size: 28 })],
  }),
];

export const buildSignatureBlock = (assinatura: ProposalDocumentSignature) => [
  new Paragraph({
    keepNext: true,
    spacing: { before: 240, after: 600 },
    children: [run(`${assinatura.local}, ${assinatura.data}.`)],
  }),
  new Paragraph({
    keepNext: true,
    alignment: AlignmentType.CENTER,
    children: [run('_______________________________________')],
  }),
  new Paragraph({
    keepNext: true,
    alignment: AlignmentType.CENTER,
    children: [run(assinatura.nome, { bold: true })],
  }),
  new Paragraph({
    keepNext: true,
    alignment: AlignmentType.CENTER,
    children: [run(`CPF: ${assinatura.cpf}`)],
  }),
  new Paragraph({ alignment: AlignmentType.CENTER, children: [run(`Cargo: ${assinatura.cargo}`)] }),
];

const sectionChildren = (
  secao: ProposalDocumentSection,
  numero: number,
  layout: ProposalTableLayout,
) => {
  const children: Array<Paragraph | Table> = [sectionTitle(numero, secao.titulo)];
  (secao.campos ?? []).forEach((campo) => children.push(fieldParagraph(campo)));
  (secao.grupos ?? []).forEach((grupo) => {
    children.push(
      new Paragraph({
        spacing: { before: 160, after: 80 },
        children: [run(grupo.titulo, { bold: true })],
      }),
    );
    grupo.campos.forEach((campo) => children.push(fieldParagraph(campo)));
  });
  (secao.paragrafos ?? []).forEach((texto) =>
    children.push(new Paragraph({ spacing: SPACING_TEXT, children: [run(texto)] })),
  );
  if (secao.tabela) {
    children.push(
      secao.tabela.linhas.length
        ? table(secao.tabela, layout)
        : new Paragraph({ children: [run('Nenhum item selecionado.')] }),
    );
  }
  return children;
};

export const proposalTableLayout = (structure: ProposalDocumentStructure) =>
  tableLayout(structure.secoes.find((secao) => secao.tabela)?.tabela?.colunas.length ?? 0);

export const renderProposalDocx = (structure: ProposalDocumentStructure) => {
  const layout = proposalTableLayout(structure);
  const numeradas = structure.secoes.flatMap((secao, index) =>
    sectionChildren(secao, index + 1, layout),
  );
  const localData = sectionTitle(structure.secoes.length + 1, 'Local, data e assinatura');

  return new Document({
    creator: 'ConLicitação',
    title: structure.titulo,
    sections: [
      {
        properties: {
          // docx troca largura e altura sozinho quando a orientação é paisagem.
          page: {
            margin: PAGE_MARGINS,
            size: {
              ...A4,
              orientation:
                layout.orientation === 'landscape'
                  ? PageOrientation.LANDSCAPE
                  : PageOrientation.PORTRAIT,
            },
          },
        },
        children: [
          ...buildDocumentHeader(structure.titulo),
          ...numeradas,
          localData,
          ...buildSignatureBlock(structure.assinatura),
        ],
      },
    ],
  });
};

export const proposalFilename = (numeroLicitacao: string, extensao: 'docx' | 'pdf') => {
  const base = numeroLicitacao
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `proposta-${base || 'licitacao'}.${extensao}`;
};

export const downloadProposalDocx = async (structure: ProposalDocumentStructure) => {
  const blob = await Packer.toBlob(renderProposalDocx(structure));
  return { blob, filename: proposalFilename(structure.numeroLicitacao, 'docx') };
};

const ALINHAMENTO_DOCX: Record<DeclarationAlign, typeof AlignmentType[keyof typeof AlignmentType]> =
  {
    left: AlignmentType.LEFT,
    center: AlignmentType.CENTER,
    right: AlignmentType.RIGHT,
    justify: AlignmentType.JUSTIFIED,
  };
const LISTA_NUMERADA = 'declaracao-numerada';

// Cada "\n" do trecho (um <br>) vira quebra de linha no mesmo parágrafo.
const declarationRuns = (trechos: DeclarationRun[]) =>
  trechos.flatMap((trecho) =>
    trecho.texto.split('\n').map(
      (parte, index) =>
        new TextRun({
          text: parte,
          font: FONT,
          size: SIZE,
          bold: trecho.negrito,
          italics: trecho.italico,
          underline: trecho.sublinhado ? {} : undefined,
          break: index > 0 ? 1 : undefined,
        }),
    ),
  );

const declarationParagraph = (
  paragrafo: DeclarationParagraph,
  lista?: { ordenada: boolean; instancia: number },
) =>
  new Paragraph({
    spacing: SPACING_TEXT,
    alignment: ALINHAMENTO_DOCX[paragrafo.alinhamento],
    children: declarationRuns(paragrafo.trechos),
    ...(lista && !lista.ordenada ? { bullet: { level: 0 } } : {}),
    ...(lista && lista.ordenada
      ? { numbering: { reference: LISTA_NUMERADA, level: 0, instance: lista.instancia } }
      : {}),
  });

export const renderDeclarationDocx = (estrutura: DeclarationStructure) => {
  let instancia = 0;
  const corpo = estrutura.blocos.flatMap((bloco) => {
    if (bloco.tipo === 'paragrafo') return [declarationParagraph(bloco)];
    instancia += 1;
    const lista = { ordenada: bloco.ordenada, instancia };
    return bloco.itens.map((item) => declarationParagraph(item, lista));
  });

  return new Document({
    creator: 'ConLicitação',
    title: estrutura.titulo,
    numbering: {
      config: [
        {
          reference: LISTA_NUMERADA,
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: { page: { margin: PAGE_MARGINS, size: A4 } },
        children: [
          ...estrutura.referencia.map(fieldParagraph),
          new Paragraph({ spacing: { after: 240 }, children: [] }),
          ...buildDocumentHeader(estrutura.titulo),
          ...corpo,
          ...buildSignatureBlock(estrutura.assinatura),
        ],
      },
    ],
  });
};

export const downloadDeclarationDocx = async (
  estrutura: DeclarationStructure,
  nomeBase: string,
) => {
  const blob = await Packer.toBlob(renderDeclarationDocx(estrutura));
  return { blob, filename: `${nomeBase}.docx` };
};
